import React, { useEffect, useState } from "react";
import {
    DemoVideo,
    EMAIL_STRICT_LOCAL_PART_KEY,
    RecipientVerificationStatus,
    SIGNUP_NOTIFICATION_EMAIL_KEY,
    UPLOAD_MAX_FILE_MB_KEY,
    deleteSetting,
    getDemoVideos,
    getRecipientVerificationStatus,
    getSetting,
    putSetting,
    requestRecipientVerification,
    saveDemoVideos,
    slugify,
    toYouTubeEmbedUrl,
} from "../../api/SettingsAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
}

/** One setting, visually self-contained: title + status badge + body. */
const SettingCard: React.FC<{
    icon: string;
    title: string;
    badge?: { text: string; tone: "on" | "off" | "neutral" };
    children: React.ReactNode;
}> = ({ icon, title, badge, children }) => (
    <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "8px", padding: "0.9rem 1rem", marginBottom: "1rem", background: "var(--bg-primary, #fff)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <span aria-hidden="true">{icon}</span>
            <strong style={{ fontSize: "1rem" }}>{title}</strong>
            {badge && (
                <span style={{
                    marginLeft: "auto",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    borderRadius: "999px",
                    padding: "0.1rem 0.6rem",
                    color: badge.tone === "on" ? "#166534" : badge.tone === "off" ? "#7f1d1d" : "#555",
                    background: badge.tone === "on" ? "#dcfce7" : badge.tone === "off" ? "#fee2e2" : "#eee",
                }}>
                    {badge.text}
                </span>
            )}
        </div>
        {children}
    </div>
);

/** Site settings (ROOT / SYS_ADMIN), one card per setting. */
const SettingsPanel: React.FC<Props> = ({ token }) => {
    // Demo videos (list; each row = title + YouTube link, shown at /demo in order)
    const [videos, setVideos] = useState<DemoVideo[]>([]);
    const [newTitle, setNewTitle] = useState("");
    const [newUrl, setNewUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    // New-account notification email
    const [notifyEmail, setNotifyEmail] = useState("");
    const [savedNotifyEmail, setSavedNotifyEmail] = useState<string | null>(null);
    const [notifyBusy, setNotifyBusy] = useState(false);
    const [notifyError, setNotifyError] = useState<string | null>(null);
    const [notifyInfo, setNotifyInfo] = useState<string | null>(null);

    useEffect(() => {
        getDemoVideos()
            .then(setVideos)
            .catch((e) => { logApiError(e); setError(getUserMessage(e)); });
        getSetting(SIGNUP_NOTIFICATION_EMAIL_KEY)
            .then((v) => { setSavedNotifyEmail(v); setNotifyEmail(v ?? ""); })
            .catch((e) => { logApiError(e); setNotifyError(getUserMessage(e)); });
    }, []);

    const saveNotifyEmail = async () => {
        setNotifyBusy(true); setNotifyError(null); setNotifyInfo(null);
        try {
            await putSetting(token, SIGNUP_NOTIFICATION_EMAIL_KEY, notifyEmail.trim());
            setSavedNotifyEmail(notifyEmail.trim());
            setNotifyInfo("Saved — new registrations will be emailed to this address.");
        } catch (e) {
            logApiError(e); setNotifyError(getUserMessage(e));
        } finally {
            setNotifyBusy(false);
        }
    };

    // Upload size limit (MB) for listing photos/HTML docs; absent row = 10
    const [uploadMb, setUploadMb] = useState("");
    const [uploadBusy, setUploadBusy] = useState(false);
    const [uploadInfo, setUploadInfo] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    useEffect(() => {
        getSetting(UPLOAD_MAX_FILE_MB_KEY)
            .then((v) => setUploadMb(v ?? "10"))
            .catch((e) => logApiError(e)); // default shows as 10
    }, []);

    const saveUploadMb = async () => {
        setUploadBusy(true); setUploadError(null); setUploadInfo(null);
        try {
            await putSetting(token, UPLOAD_MAX_FILE_MB_KEY, uploadMb.trim());
            setUploadInfo(`Saved — uploads up to ${uploadMb.trim()} MB are allowed from now on.`);
        } catch (e) {
            logApiError(e); setUploadError(getUserMessage(e));
        } finally {
            setUploadBusy(false);
        }
    };

    // Strict registration-email policy (blocks '+', etc.); absent row = on
    const [strictEmail, setStrictEmail] = useState(true);
    const [strictBusy, setStrictBusy] = useState(false);
    const [strictInfo, setStrictInfo] = useState<string | null>(null);
    const [strictError, setStrictError] = useState<string | null>(null);

    useEffect(() => {
        getSetting(EMAIL_STRICT_LOCAL_PART_KEY)
            .then((v) => setStrictEmail(v !== "false"))
            .catch((e) => logApiError(e)); // default stays on
    }, []);

    const toggleStrictEmail = async (on: boolean) => {
        setStrictBusy(true);
        setStrictInfo(null);
        setStrictError(null);
        try {
            await putSetting(token, EMAIL_STRICT_LOCAL_PART_KEY, on ? "true" : "false");
            setStrictEmail(on);
            setStrictInfo(on
                ? "Saved — strict registration emails are ON."
                : "Saved — strict registration emails are OFF.");
        } catch (e) {
            logApiError(e); setStrictError(getUserMessage(e));
        } finally {
            setStrictBusy(false);
        }
    };

    // Test-recipient verification (SES sandbox on local/dev)
    const [verifyEmail, setVerifyEmail] = useState("");
    const [verifyStatus, setVerifyStatus] = useState<RecipientVerificationStatus | null>(null);
    const [verifyBusy, setVerifyBusy] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);

    const sendVerification = async () => {
        setVerifyBusy(true); setVerifyError(null); setVerifyStatus(null);
        try {
            await requestRecipientVerification(token, verifyEmail.trim());
            setVerifyStatus("PENDING");
        } catch (e) {
            logApiError(e); setVerifyError(getUserMessage(e));
        } finally {
            setVerifyBusy(false);
        }
    };

    const checkVerification = async () => {
        setVerifyBusy(true); setVerifyError(null);
        try {
            setVerifyStatus(await getRecipientVerificationStatus(token, verifyEmail.trim()));
        } catch (e) {
            logApiError(e); setVerifyError(getUserMessage(e));
        } finally {
            setVerifyBusy(false);
        }
    };

    const removeNotifyEmail = async () => {
        if (!window.confirm("Stop emailing about new registrations?")) return;
        setNotifyBusy(true); setNotifyError(null); setNotifyInfo(null);
        try {
            await deleteSetting(token, SIGNUP_NOTIFICATION_EMAIL_KEY);
            setSavedNotifyEmail(null);
            setNotifyEmail("");
            setNotifyInfo("Removed — no more new-account emails.");
        } catch (e) {
            logApiError(e); setNotifyError(getUserMessage(e));
        } finally {
            setNotifyBusy(false);
        }
    };

    const newEmbed = newUrl.trim() ? toYouTubeEmbedUrl(newUrl) : null;

    /** Every list change (add / remove / move) is persisted immediately. */
    const persistVideos = async (next: DemoVideo[], message: string) => {
        setBusy(true); setError(null); setInfo(null);
        try {
            await saveDemoVideos(token, next);
            setVideos(next);
            setInfo(message);
        } catch (e) {
            logApiError(e); setError(getUserMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const addVideo = async () => {
        // A titled video gets a direct link at /demo/{slug}; de-dupe by
        // suffixing (_2, _3, ...) so two "Home Rental" videos both work.
        let slug = slugify(newTitle);
        if (slug) {
            const taken = new Set(videos.map((v) => v.slug).filter(Boolean));
            let candidate = slug;
            for (let n = 2; taken.has(candidate); n++) candidate = `${slug}_${n}`;
            slug = candidate;
        }
        const video: DemoVideo = { title: newTitle.trim(), url: newUrl.trim(), slug: slug || undefined };
        await persistVideos([...videos, video],
            slug ? `Added — direct link: /demo/${slug}` : "Added — it's live at /demo.");
        setNewTitle("");
        setNewUrl("");
    };

    const removeVideo = (index: number) => {
        const v = videos[index];
        if (!window.confirm(`Remove "${v.title || v.url}" from the demo page?`)) return;
        persistVideos(videos.filter((_, i) => i !== index), "Removed.");
    };

    const moveVideo = (index: number, delta: -1 | 1) => {
        const next = [...videos];
        const [moved] = next.splice(index, 1);
        next.splice(index + delta, 0, moved);
        persistVideos(next, "Order saved.");
    };

    return (
        <div className="section-box section-box--narrow">
            <h2 style={{ marginBottom: "0.25rem" }}>Site Settings</h2>
            <div className="item-description" style={{ marginBottom: "1rem" }}>
                Stored in the database and applied immediately — no restarts.
            </div>

            <SettingCard
                icon="🎬"
                title="Demo videos"
                badge={videos.length > 0 ? { text: `${videos.length} SET`, tone: "on" } : { text: "NOT SET", tone: "neutral" }}
            >
                <div className="item-description" style={{ marginBottom: "0.5rem" }}>
                    YouTube videos shown at /demo, in this order (the first one also
                    powers the “▶ Watch demo” button on the landing page). Accepts
                    youtu.be, watch?v= and shorts links. Changes save immediately.
                </div>

                {videos.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "0.75rem" }}>
                        {videos.map((v, i) => (
                            <div key={`${v.url}-${i}`} style={{ display: "flex", alignItems: "center", gap: "0.5rem", border: "1px solid var(--border-color, #eee)", borderRadius: "6px", padding: "0.35rem 0.5rem" }}>
                                <span style={{ color: "var(--text-secondary)", minWidth: "1.2rem" }}>{i + 1}.</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {v.title ? <strong>{v.title}</strong> : <em style={{ color: "var(--text-secondary)" }}>(no title)</em>}
                                    {v.slug && (
                                        <a href={`/demo/${v.slug}`} target="_blank" rel="noopener noreferrer"
                                           style={{ marginLeft: "0.5rem", fontSize: "0.85rem" }}>
                                            /demo/{v.slug}
                                        </a>
                                    )}
                                    <span style={{ color: "var(--text-secondary)", marginLeft: "0.5rem", fontSize: "0.85rem" }}>{v.url}</span>
                                </span>
                                <button className="btn-sm" onClick={() => moveVideo(i, -1)} disabled={busy || i === 0} title="Move up">↑</button>
                                <button className="btn-sm" onClick={() => moveVideo(i, 1)} disabled={busy || i === videos.length - 1} title="Move down">↓</button>
                                <button className="btn-sm" onClick={() => removeVideo(i)} disabled={busy} title="Remove">✕</button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>Title (shown above the player)</label>
                    <input
                        type="text"
                        value={newTitle}
                        onChange={(e) => { setNewTitle(e.target.value); setInfo(null); }}
                        placeholder="e.g. Searching for a plumber"
                        disabled={busy}
                    />
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <label>YouTube link</label>
                    <input
                        type="text"
                        value={newUrl}
                        onChange={(e) => { setNewUrl(e.target.value); setInfo(null); }}
                        placeholder="e.g. https://youtu.be/XXXXXXXXXXX"
                        disabled={busy}
                    />
                    {newUrl.trim() !== "" && !newEmbed && (
                        <div className="error-message" style={{ marginTop: "0.25rem" }}>
                            That does not look like a YouTube link.
                        </div>
                    )}
                </div>
                <div className="button-group">
                    <button onClick={addVideo} disabled={busy || !newEmbed}>
                        {busy ? "Saving…" : "+ Add video"}
                    </button>
                </div>
                {error && <div className="error-message" style={{ marginTop: "0.5rem" }}>{error}</div>}
                {info && <div className="success-message" style={{ marginTop: "0.5rem" }}>{info}</div>}
                {newEmbed && (
                    <div style={{ marginTop: "0.75rem" }}>
                        <div style={{ fontWeight: 600, marginBottom: "0.35rem" }}>Preview (not added yet)</div>
                        <div style={{ position: "relative", paddingTop: "56.25%" }}>
                            <iframe
                                src={newEmbed}
                                title="Demo video preview"
                                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, borderRadius: "8px" }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </div>
                    </div>
                )}
            </SettingCard>

            <SettingCard
                icon="📣"
                title="New-account notification"
                badge={savedNotifyEmail ? { text: "SET", tone: "on" } : { text: "NOT SET", tone: "neutral" }}
            >
                <div className="item-description" style={{ marginBottom: "0.5rem" }}>
                    Every new registration sends a summary email (org, user, email,
                    system) to this address. Leave unset for no emails.
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <input
                        type="email"
                        value={notifyEmail}
                        onChange={(e) => { setNotifyEmail(e.target.value); setNotifyInfo(null); }}
                        placeholder="e.g. admin@babahanks.com"
                        disabled={notifyBusy}
                    />
                </div>
                <div className="button-group">
                    <button
                        onClick={saveNotifyEmail}
                        disabled={notifyBusy || !notifyEmail.trim() || notifyEmail.trim() === (savedNotifyEmail ?? "")}
                    >
                        {notifyBusy ? "Saving…" : "Save"}
                    </button>
                    {savedNotifyEmail && (
                        <button className="btn-secondary" onClick={removeNotifyEmail} disabled={notifyBusy}>
                            Remove
                        </button>
                    )}
                </div>
                {notifyError && <div className="error-message" style={{ marginTop: "0.5rem" }}>{notifyError}</div>}
                {notifyInfo && <div className="success-message" style={{ marginTop: "0.5rem" }}>{notifyInfo}</div>}
            </SettingCard>

            <SettingCard
                icon="✉"
                title="Strict registration emails"
                badge={strictEmail ? { text: "ON", tone: "on" } : { text: "OFF", tone: "off" }}
            >
                <div className="item-description" style={{ marginBottom: "0.5rem" }}>
                    Rejects “+” and other special characters in new registrations, so
                    one mailbox can't create many account variations (user+1@,
                    user+2@ …). Letters, digits, dots, hyphens and underscores only.
                    Existing accounts and login are unaffected.
                </div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input
                        type="checkbox"
                        checked={strictEmail}
                        onChange={(e) => toggleStrictEmail(e.target.checked)}
                        disabled={strictBusy}
                    />
                    Enforce strict registration emails
                    <span className="item-description">(saves as soon as you toggle)</span>
                </label>
                {strictError && <div className="error-message" style={{ marginTop: "0.5rem" }}>{strictError}</div>}
                {strictInfo && <div className="success-message" style={{ marginTop: "0.5rem" }}>{strictInfo}</div>}
            </SettingCard>

            <SettingCard
                icon="📎"
                title="Upload size limit"
                badge={{ text: `${uploadMb || "10"} MB`, tone: "neutral" }}
            >
                <div className="item-description" style={{ marginBottom: "0.5rem" }}>
                    Maximum size for listing photos and HTML pages. Applies
                    immediately — no restart. The servers' hard ceiling is 25 MB;
                    values above it will still be rejected by the server.
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <input
                        type="number"
                        min={1}
                        max={25}
                        value={uploadMb}
                        onChange={(e) => { setUploadMb(e.target.value); setUploadInfo(null); }}
                        placeholder="10"
                        disabled={uploadBusy}
                        style={{ width: "6rem" }}
                    />
                    <span style={{ marginLeft: "0.4rem" }}>MB</span>
                </div>
                <div className="button-group">
                    <button
                        onClick={saveUploadMb}
                        disabled={uploadBusy || !uploadMb.trim() || Number(uploadMb) < 1 || Number(uploadMb) > 25}
                    >
                        {uploadBusy ? "Saving…" : "Save"}
                    </button>
                </div>
                {uploadError && <div className="error-message" style={{ marginTop: "0.5rem" }}>{uploadError}</div>}
                {uploadInfo && <div className="success-message" style={{ marginTop: "0.5rem" }}>{uploadInfo}</div>}
            </SettingCard>

            <SettingCard icon="🧪" title="Verify a test recipient" badge={{ text: "DEV TOOL", tone: "neutral" }}>
                <div className="item-description" style={{ marginBottom: "0.5rem" }}>
                    Local/dev only: the sandboxed email account can only deliver to
                    verified addresses, so a tester can't complete registration until
                    verified. “Send verification” makes AWS email them a confirmation
                    link; once clicked and VERIFIED, they can register. Not needed on
                    production.
                </div>
                <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                    <input
                        type="email"
                        value={verifyEmail}
                        onChange={(e) => { setVerifyEmail(e.target.value); setVerifyStatus(null); }}
                        placeholder="tester@example.com"
                        disabled={verifyBusy}
                    />
                </div>
                <div className="button-group">
                    <button onClick={sendVerification} disabled={verifyBusy || !verifyEmail.includes("@")}>
                        {verifyBusy ? "Working…" : "Send verification"}
                    </button>
                    <button className="btn-secondary" onClick={checkVerification} disabled={verifyBusy || !verifyEmail.includes("@")}>
                        Check status
                    </button>
                    {verifyStatus && (
                        <span style={{ alignSelf: "center", fontWeight: 600, color: verifyStatus === "VERIFIED" ? "var(--success, #2e7d32)" : "var(--text-secondary)" }}>
                            {verifyStatus === "NOT_REQUESTED" ? "Not requested yet" : verifyStatus}
                        </span>
                    )}
                </div>
                {verifyError && <div className="error-message" style={{ marginTop: "0.5rem" }}>{verifyError}</div>}
            </SettingCard>
        </div>
    );
};

export default SettingsPanel;